import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  ArrowLeft,
  ExternalLink,
  Clock,
  FileText,
  Cpu,
  Pencil,
  X,
  Save,
  Plus,
  Trash2,
  CheckCircle,
  Lock,
  History,
  AlertTriangle,
  Archive,
  User,
  FileUp,
} from 'lucide-react'
import type {
  ReviewDetailResponse,
  UpdateReviewResponse,
  ApproveReviewResponse,
  ArchiveReviewResponse,
  ReviewHistoryResponse,
  ReviewData,
  Finding,
  UserEntityControl,
  Subservice,
} from '@common/types'
import { EditHistoryTimeline } from '@frontend/components/history'
import { ReportTypeBadge } from '@frontend/components/ui/report-type-badge'
import { api, fetchAuditors } from '@frontend/lib/api'
import { cn, formatDate, formatDateTime } from '@frontend/lib/utils'
import { usePermissions } from '@frontend/hooks/usePermissions'
import { AppLayout } from '@frontend/components/layout/AppLayout'
import { Card, CardContent, CardHeader, CardTitle } from '@frontend/components/ui/card'
import { Button } from '@frontend/components/ui/button'
import { Spinner } from '@frontend/components/ui/spinner'
import { ReviewStatusBadge, ValidityBadge, AuditorStatusBadge } from '@frontend/components/ui/status-badge'
import { calculateValidityStatus } from '@common/utils/validity'
import { MetadataItem } from '@frontend/components/ui/metadata-item'
import { DocumentsSection } from '@frontend/components/documents/DocumentsSection'
import { UploadBridgeLetterModal } from '@frontend/components/vendors/UploadBridgeLetterModal'
import { BridgeLetterCard } from '@frontend/components/vendors/BridgeLetterCard'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@frontend/components/ui/dialog'

async function fetchReview(id: string): Promise<ReviewDetailResponse> {
  return api.get(`reviews/${id}`).json<ReviewDetailResponse>()
}

async function updateReview(id: string, reviewData: ReviewData, auditorId: string): Promise<UpdateReviewResponse> {
  return api.put(`reviews/${id}`, { json: { reviewData, auditorId } }).json<UpdateReviewResponse>()
}

async function approveReview(id: string): Promise<ApproveReviewResponse> {
  return api.post(`reviews/${id}/approve`).json<ApproveReviewResponse>()
}

async function archiveReviewApi(id: string): Promise<ArchiveReviewResponse> {
  return api.post(`reviews/${id}/archive`).json<ArchiveReviewResponse>()
}

async function fetchReviewHistory(id: string): Promise<ReviewHistoryResponse> {
  return api.get(`reviews/${id}/history`).json<ReviewHistoryResponse>()
}

// ============ Display Components (Read-only) ============

function formatPageNumbers(pageNumbers?: number[]): string | null {
  if (!pageNumbers || pageNumbers.length === 0) return null
  const sorted = [...pageNumbers].sort((a, b) => a - b)
  if (sorted.length === 1) return `(p. ${sorted[0]})`
  return `(pp. ${sorted.join(', ')})`
}

function PageCitation({ pageNumbers }: { pageNumbers?: number[] }) {
  const formatted = formatPageNumbers(pageNumbers)
  if (!formatted) return null
  return <span className="text-xs text-muted-foreground ml-2">{formatted}</span>
}

function SeverityBadge({ severity }: { severity?: string }) {
  if (!severity) return null

  const severityLower = severity.toLowerCase()
  let colorClass = 'bg-slate-100 text-slate-700'
  if (severityLower === 'high' || severityLower === 'critical') {
    colorClass = 'bg-red-100 text-red-700'
  } else if (severityLower === 'medium' || severityLower === 'moderate') {
    colorClass = 'bg-amber-100 text-amber-700'
  } else if (severityLower === 'low') {
    colorClass = 'bg-emerald-100 text-emerald-700'
  }

  return (
    <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ${colorClass}`}>
      {severity}
    </span>
  )
}

function FindingsDisplay({ findings }: { findings: Finding[] }) {
  if (findings.length === 0) return <span className="text-muted-foreground">None</span>

  return (
    <div className="space-y-3">
      {findings.map((finding, i) => (
        <div key={i} className="bg-muted/50 rounded-lg p-3">
          <div className="flex items-start justify-between gap-2">
            <div className="font-medium text-sm">
              {finding.title || `Finding ${i + 1}`}
              <PageCitation pageNumbers={finding.pageNumbers} />
            </div>
            <SeverityBadge severity={finding.severity} />
          </div>
          {finding.description && <p className="text-sm text-muted-foreground mt-1">{finding.description}</p>}
          {finding.note && (
            <div className="mt-2 border-l-2 border-primary/40 pl-2">
              <p className="text-sm">
                <span className="font-medium">Note:</span> {finding.note}
              </p>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

function SubservicesDisplay({ subservices }: { subservices: Subservice[] }) {
  if (subservices.length === 0) return <span className="text-muted-foreground">None</span>

  return (
    <div className="space-y-2">
      {subservices.map((sub, i) => (
        <div key={i} className="bg-muted/50 rounded-lg p-3">
          <div className="font-medium text-sm">
            {sub.name || `Subservice ${i + 1}`}
            <PageCitation pageNumbers={sub.pageNumbers} />
          </div>
          {sub.description && <p className="text-sm text-muted-foreground mt-1">{sub.description}</p>}
        </div>
      ))}
    </div>
  )
}

function UserEntityControlsDisplay({ controls }: { controls: UserEntityControl[] }) {
  if (controls.length === 0) return <span className="text-muted-foreground">None</span>

  return (
    <div className="space-y-2">
      {controls.map((control, i) => (
        <div key={i} className="bg-muted/50 rounded-lg p-3">
          <p className="text-sm">
            {control.description || `Control ${i + 1}`}
            <PageCitation pageNumbers={control.pageNumbers} />
          </p>
          {control.impact && (
            <p className="text-sm text-muted-foreground mt-1">
              <span className="font-medium">Impact:</span> {control.impact}
            </p>
          )}
        </div>
      ))}
    </div>
  )
}

// ============ Edit Components ============

function TextInput({
  value,
  onChange,
  placeholder,
}: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
    />
  )
}

function TextAreaInput({
  value,
  onChange,
  placeholder,
  rows = 3,
}: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
  rows?: number
}) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring resize-y"
    />
  )
}

function DateInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  // Convert ISO date to YYYY-MM-DD for input
  const inputValue = value ? value.split('T')[0] : ''
  return (
    <input
      type="date"
      value={inputValue}
      onChange={(e) => onChange(e.target.value)}
      className="px-3 py-2 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
    />
  )
}

function PageNumbersInput({ value, onChange }: { value?: number[]; onChange: (v: number[]) => void }) {
  // Convert number array to comma-separated string for editing
  const textValue = value?.join(', ') || ''

  const handleChange = (text: string) => {
    if (text.trim() === '') {
      onChange([])
      return
    }
    // Parse comma-separated numbers, filtering out invalid values
    const numbers = text
      .split(',')
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => !isNaN(n) && n > 0)
    onChange(numbers)
  }

  return (
    <input
      type="text"
      value={textValue}
      onChange={(e) => handleChange(e.target.value)}
      placeholder="Page numbers (e.g., 12, 45, 67)"
      className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
    />
  )
}

function StringArrayEditor({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const addItem = () => onChange([...value, ''])
  const removeItem = (index: number) => onChange(value.filter((_, i) => i !== index))
  const updateItem = (index: number, newValue: string) => {
    const updated = [...value]
    updated[index] = newValue
    onChange(updated)
  }

  return (
    <div className="space-y-2">
      {value.map((item, i) => (
        <div key={i} className="flex gap-2">
          <input
            type="text"
            value={item}
            onChange={(e) => updateItem(i, e.target.value)}
            className="flex-1 px-3 py-2 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(i)}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={addItem}>
        <Plus className="h-4 w-4 mr-1" /> Add
      </Button>
    </div>
  )
}

function FindingsEditor({ value, onChange }: { value: Finding[]; onChange: (v: Finding[]) => void }) {
  const addItem = () => onChange([...value, { title: '', description: '' }])
  const removeItem = (index: number) => onChange(value.filter((_, i) => i !== index))
  const updateItem = (index: number, field: string, newValue: string | number[]) => {
    const updated = [...value]
    updated[index] = { ...updated[index], [field]: newValue }
    onChange(updated)
  }

  return (
    <div className="space-y-3">
      {value.map((item, i) => (
        <div key={i} className="bg-muted/30 rounded-lg p-3 space-y-2">
          <div className="flex justify-between items-start">
            <span className="text-xs font-medium text-muted-foreground">Finding {i + 1}</span>
            <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(i)}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
          <TextInput value={item.title || ''} onChange={(v) => updateItem(i, 'title', v)} placeholder="Title" />
          <TextAreaInput
            value={item.description || ''}
            onChange={(v) => updateItem(i, 'description', v)}
            placeholder="Description"
            rows={2}
          />
          <select
            value={item.severity || ''}
            onChange={(e) => updateItem(i, 'severity', e.target.value)}
            className="px-3 py-2 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="">Select severity</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>
          <PageNumbersInput value={item.pageNumbers} onChange={(v) => updateItem(i, 'pageNumbers', v)} />
          <TextAreaInput
            value={item.note || ''}
            onChange={(v) => updateItem(i, 'note', v)}
            placeholder="Note (optional)"
            rows={2}
          />
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={addItem}>
        <Plus className="h-4 w-4 mr-1" /> Add Finding
      </Button>
    </div>
  )
}

function SubservicesEditor({ value, onChange }: { value: Subservice[]; onChange: (v: Subservice[]) => void }) {
  const addItem = () => onChange([...value, { name: '', description: '' }])
  const removeItem = (index: number) => onChange(value.filter((_, i) => i !== index))
  const updateItem = (index: number, field: string, newValue: string | number[]) => {
    const updated = [...value]
    updated[index] = { ...updated[index], [field]: newValue }
    onChange(updated)
  }

  return (
    <div className="space-y-3">
      {value.map((item, i) => (
        <div key={i} className="bg-muted/30 rounded-lg p-3 space-y-2">
          <div className="flex justify-between items-start">
            <span className="text-xs font-medium text-muted-foreground">Subservice {i + 1}</span>
            <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(i)}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
          <TextInput value={item.name || ''} onChange={(v) => updateItem(i, 'name', v)} placeholder="Name" />
          <TextAreaInput
            value={item.description || ''}
            onChange={(v) => updateItem(i, 'description', v)}
            placeholder="Description"
            rows={2}
          />
          <PageNumbersInput value={item.pageNumbers} onChange={(v) => updateItem(i, 'pageNumbers', v)} />
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={addItem}>
        <Plus className="h-4 w-4 mr-1" /> Add Subservice
      </Button>
    </div>
  )
}

function UserEntityControlsEditor({
  value,
  onChange,
}: {
  value: UserEntityControl[]
  onChange: (v: UserEntityControl[]) => void
}) {
  const addItem = () => onChange([...value, { description: '' }])
  const removeItem = (index: number) => onChange(value.filter((_, i) => i !== index))
  const updateItem = (index: number, field: string, newValue: string | number[]) => {
    const updated = [...value]
    updated[index] = { ...updated[index], [field]: newValue }
    onChange(updated)
  }

  return (
    <div className="space-y-3">
      {value.map((item, i) => (
        <div key={i} className="bg-muted/30 rounded-lg p-3 space-y-2">
          <div className="flex justify-between items-start">
            <span className="text-xs font-medium text-muted-foreground">Control {i + 1}</span>
            <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(i)}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
          <TextAreaInput
            value={item.description || ''}
            onChange={(v) => updateItem(i, 'description', v)}
            placeholder="Description"
            rows={2}
          />
          <TextInput
            value={item.impact || ''}
            onChange={(v) => updateItem(i, 'impact', v)}
            placeholder="Impact (optional)"
          />
          <PageNumbersInput value={item.pageNumbers} onChange={(v) => updateItem(i, 'pageNumbers', v)} />
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={addItem}>
        <Plus className="h-4 w-4 mr-1" /> Add Control
      </Button>
    </div>
  )
}

// ============ Field Wrapper ============

interface DataFieldProps {
  label: string
  value: unknown
  fieldKey: string
  isEditing: boolean
  onUpdate: (key: string, value: unknown) => void
  className?: string
}

const DATE_FIELDS = ['reportIssueDate', 'auditPeriodStart', 'auditPeriodEnd']
const TEXTAREA_FIELDS = [
  'productDescription',
  'auditorCredibility',
  'auditorOpinion',
  'auditorConclusion',
  'conclusionSummary',
]

function DataField({ label, value, fieldKey, isEditing, onUpdate, className }: DataFieldProps) {
  if (!isEditing && (value === null || value === undefined || value === '')) {
    return null
  }

  const renderEditField = () => {
    // Array of strings (trustCriteria)
    if (fieldKey === 'trustCriteria') {
      return <StringArrayEditor value={(value as string[]) || []} onChange={(v) => onUpdate(fieldKey, v)} />
    }

    // Structured arrays
    if (fieldKey === 'findings') {
      return <FindingsEditor value={(value as Finding[]) || []} onChange={(v) => onUpdate(fieldKey, v)} />
    }
    if (fieldKey === 'subservicesUsage') {
      return <SubservicesEditor value={(value as Subservice[]) || []} onChange={(v) => onUpdate(fieldKey, v)} />
    }
    if (fieldKey === 'userEntityControls') {
      return (
        <UserEntityControlsEditor
          value={(value as UserEntityControl[]) || []}
          onChange={(v) => onUpdate(fieldKey, v)}
        />
      )
    }

    // Date fields
    if (DATE_FIELDS.includes(fieldKey)) {
      return <DateInput value={(value as string) || ''} onChange={(v) => onUpdate(fieldKey, v)} />
    }

    // Textarea fields
    if (TEXTAREA_FIELDS.includes(fieldKey)) {
      return <TextAreaInput value={(value as string) || ''} onChange={(v) => onUpdate(fieldKey, v)} />
    }

    // Default: text input for strings
    if (typeof value === 'string' || value === null || value === undefined) {
      return <TextInput value={(value as string) || ''} onChange={(v) => onUpdate(fieldKey, v)} />
    }

    // Fallback for other types: JSON textarea
    return (
      <TextAreaInput
        value={JSON.stringify(value, null, 2)}
        onChange={(v) => {
          try {
            onUpdate(fieldKey, JSON.parse(v))
          } catch {
            // Invalid JSON, don't update
          }
        }}
        rows={4}
      />
    )
  }

  const renderDisplayValue = () => {
    // Handle specific structured array fields
    if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
      if (fieldKey === 'findings') {
        return <FindingsDisplay findings={value as Finding[]} />
      }
      if (fieldKey === 'subservicesUsage') {
        return <SubservicesDisplay subservices={value as Subservice[]} />
      }
      if (fieldKey === 'userEntityControls') {
        return <UserEntityControlsDisplay controls={value as UserEntityControl[]} />
      }
    }

    if (Array.isArray(value)) {
      if (value.length === 0) return <span className="text-muted-foreground">None</span>
      return (
        <ul className="list-disc list-inside space-y-1">
          {value.map((item, i) => (
            <li key={i} className="text-sm">
              {typeof item === 'object' ? JSON.stringify(item) : String(item)}
            </li>
          ))}
        </ul>
      )
    }
    if (typeof value === 'object' && value !== null) {
      return <pre className="text-sm bg-muted p-2 rounded overflow-x-auto">{JSON.stringify(value, null, 2)}</pre>
    }
    return <span className="text-sm">{String(value)}</span>
  }

  return (
    <div className={cn('py-3 border-b border-border/50 last:border-0', className)}>
      <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">{label}</div>
      {isEditing ? renderEditField() : renderDisplayValue()}
    </div>
  )
}

// ============ Main Component ============

export function ReviewDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { can } = usePermissions()

  const [isEditing, setIsEditing] = useState(false)
  const [editedData, setEditedData] = useState<ReviewData | null>(null)
  const [editedAuditorId, setEditedAuditorId] = useState<string | null>(null)
  const [showApproveDialog, setShowApproveDialog] = useState(false)
  const [showArchiveDialog, setShowArchiveDialog] = useState(false)
  const [bridgeModalOpen, setBridgeModalOpen] = useState(false)

  const { data, isLoading, error } = useQuery({
    queryKey: ['review', id],
    queryFn: () => fetchReview(id!),
    enabled: !!id,
  })

  const { data: historyData } = useQuery({
    queryKey: ['review', id, 'history'],
    queryFn: () => fetchReviewHistory(id!),
    enabled: !!id,
  })

  const { data: auditorsData } = useQuery({
    queryKey: ['auditors'],
    queryFn: fetchAuditors,
    enabled: isEditing,
  })

  const updateMutation = useMutation({
    mutationFn: ({ reviewData, auditorId }: { reviewData: ReviewData; auditorId: string }) =>
      updateReview(id!, reviewData, auditorId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['review', id] })
      queryClient.invalidateQueries({ queryKey: ['review', id, 'history'] })
      setIsEditing(false)
      setEditedData(null)
      setEditedAuditorId(null)
    },
  })

  const approveMutation = useMutation({
    mutationFn: () => approveReview(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['review', id] })
      queryClient.invalidateQueries({ queryKey: ['reviews'] })
    },
  })

  const archiveMutation = useMutation({
    mutationFn: () => archiveReviewApi(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['review', id] })
      queryClient.invalidateQueries({ queryKey: ['reviews'] })
    },
  })

  const review = data?.review
  const isArchived = !!review?.archivedAt
  const isProcessing = review?.status === 'pending' || review?.status === 'extracting'
  const isApproved = review?.status === 'complete'

  // Permission-based action flags
  const canEditStatus = review?.status === 'ai_complete' && !isArchived
  const canEdit = canEditStatus && can('reviews.edit')
  const hasUnpublishedAuditor = !!review?.auditorId && review?.auditorStatus !== 'published'
  const canApprove = canEditStatus && can('reviews.approve') && !hasUnpublishedAuditor
  const canArchive = !isArchived && !isProcessing && can('reviews.archive')

  const handleStartEdit = () => {
    setEditedData(structuredClone(review?.reviewData) || null)
    setEditedAuditorId(review?.auditorId ?? null)
    setIsEditing(true)
  }

  const handleCancelEdit = () => {
    setIsEditing(false)
    setEditedData(null)
    setEditedAuditorId(null)
  }

  const handleSave = () => {
    if (editedData && editedAuditorId) {
      updateMutation.mutate({ reviewData: editedData, auditorId: editedAuditorId })
    }
  }

  const handleFieldUpdate = (key: string, value: unknown) => {
    setEditedData((prev) => (prev ? ({ ...prev, [key]: value } as ReviewData) : null))
  }

  const handleApprove = () => {
    approveMutation.mutate()
    setShowApproveDialog(false)
  }

  const handleArchive = () => {
    archiveMutation.mutate()
    setShowArchiveDialog(false)
  }

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex justify-center py-16">
          <Spinner size="lg" />
        </div>
      </AppLayout>
    )
  }

  if (error || !review) {
    return (
      <AppLayout>
        <div className="py-16 text-center">
          <p className="text-destructive">Failed to load review.</p>
          <Button variant="ghost" className="mt-4" onClick={() => navigate('/reviews')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Reviews
          </Button>
        </div>
      </AppLayout>
    )
  }

  const displayData = isEditing ? editedData : review.reviewData

  // Bridge letter: upload unlocks once the review is approved and its report is
  // expired (daysRemaining <= 0) and no active bridge letter exists yet. The
  // approved-status gate mirrors the server, which only accepts bridge letters
  // on 'complete' reviews.
  const bridgeAuditPeriodEnd = review.reviewData?.auditPeriodEnd ?? null
  const bridgeValidity = calculateValidityStatus(bridgeAuditPeriodEnd)
  const reportExpired = bridgeValidity.status !== 'unknown' && bridgeValidity.daysRemaining <= 0
  const canUploadBridge = isApproved && reportExpired && !review.bridgeLetter && can('reviews.create') && !isArchived

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Back button */}
        <Button variant="ghost" size="sm" onClick={() => navigate('/reviews')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Reviews
        </Button>

        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{review.vendorName || 'Unknown Vendor'}</h1>
            <div className="flex flex-wrap items-center gap-3 mt-2">
              <ReviewStatusBadge status={review.status} />
              <ReportTypeBadge reportType={review.reportType} />
              {review.reviewData && (
                <ValidityBadge
                  auditPeriodEnd={review.reviewData.auditPeriodEnd}
                  bridgeLetter={review.bridgeLetter}
                  isLatestReview={review.isLatestReview}
                />
              )}
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex gap-2">
            {/* Edit mode buttons */}
            {isEditing && (
              <>
                <Button variant="outline" onClick={handleCancelEdit} disabled={updateMutation.isPending}>
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={updateMutation.isPending}>
                  {updateMutation.isPending ? (
                    <Spinner size="sm" className="mr-2" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save Changes
                </Button>
              </>
            )}
            {/* View mode buttons - permission-based */}
            {!isEditing && !isArchived && (
              <>
                {canEdit && (
                  <Button variant="outline" onClick={handleStartEdit}>
                    <Pencil className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                )}
                {canApprove && (
                  <Button onClick={() => setShowApproveDialog(true)} disabled={approveMutation.isPending}>
                    {approveMutation.isPending ? (
                      <Spinner size="sm" className="mr-2" />
                    ) : (
                      <CheckCircle className="h-4 w-4 mr-2" />
                    )}
                    Approve
                  </Button>
                )}
                {canArchive && (
                  <Button
                    variant="outline"
                    onClick={() => setShowArchiveDialog(true)}
                    disabled={archiveMutation.isPending}
                  >
                    {archiveMutation.isPending ? (
                      <Spinner size="sm" className="mr-2" />
                    ) : (
                      <Archive className="h-4 w-4 mr-2" />
                    )}
                    Archive
                  </Button>
                )}
              </>
            )}
            {/* Status badges */}
            {isArchived && (
              <div className="flex items-center gap-2 text-slate-700 bg-slate-500/10 px-3 py-1.5 rounded-md">
                <Archive className="h-4 w-4" />
                <span className="text-sm font-medium">Archived</span>
              </div>
            )}
            {!isArchived && isApproved && (
              <div className="flex items-center gap-2 text-emerald-700 bg-emerald-500/10 px-3 py-1.5 rounded-md">
                <Lock className="h-4 w-4" />
                <span className="text-sm font-medium">Approved</span>
              </div>
            )}
          </div>
        </div>

        {/* Error messages */}
        {updateMutation.isError && (
          <div className="bg-destructive/10 text-destructive px-4 py-3 rounded-md text-sm">
            Failed to save changes. Please try again.
          </div>
        )}
        {approveMutation.isError && (
          <div className="bg-destructive/10 text-destructive px-4 py-3 rounded-md text-sm">
            Failed to approve review. Please try again.
          </div>
        )}

        {/* Metadata Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-medium">Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <MetadataItem icon={Clock} label="Created" value={formatDateTime(review.createdAt)} />
              {review.sourceUrl && (
                <MetadataItem
                  icon={FileText}
                  label="Source"
                  value={
                    <a
                      href={review.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline inline-flex items-center gap-1"
                    >
                      View PDF <ExternalLink className="h-3 w-3" />
                    </a>
                  }
                />
              )}
              {review.extractionMetadata && (
                <>
                  <MetadataItem icon={Cpu} label="Model" value={review.extractionMetadata.model} />
                  <MetadataItem icon={FileText} label="Pages" value={`${review.extractionMetadata.pageCount} pages`} />
                </>
              )}
              <MetadataItem icon={User} label="Created by" value={review.createdBy ?? 'Unknown'} />
              {review.approvedBy && (
                <MetadataItem
                  icon={Clock}
                  label="Approved"
                  value={`${review.approvedBy} on ${formatDate(review.approvedAt!)}`}
                />
              )}
              {review.archivedBy && (
                <MetadataItem
                  icon={Archive}
                  label="Archived"
                  value={`${review.archivedBy} on ${formatDate(review.archivedAt!)}`}
                />
              )}
              <MetadataItem
                icon={User}
                label="Auditor"
                value={
                  review.auditorId ? (
                    <span className="inline-flex items-center gap-2">
                      <Link to={`/auditors/${review.auditorId}`} className="text-primary hover:underline">
                        {review.auditorName}
                      </Link>
                      <AuditorStatusBadge status={review.auditorStatus!} />
                    </span>
                  ) : (
                    <span className="text-muted-foreground">No auditor linked</span>
                  )
                }
              />
            </div>
            {/* Unpublished auditor warning */}
            {hasUnpublishedAuditor && canEditStatus && (
              <div className="mt-4 flex items-start gap-3 rounded-md bg-amber-500/10 px-4 py-3 text-amber-700">
                <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <span className="font-medium">Approval blocked:</span> The linked auditor must be{' '}
                  <Link to={`/auditors/${review.auditorId}`} className="text-primary hover:underline font-medium">
                    published
                  </Link>{' '}
                  before this review can be approved.
                </div>
              </div>
            )}
            {/* Bridge report nudge — only while the review is unpublished. Once published, the
                "Report expired" block below drives the bridge-letter flow, so this would just duplicate it. */}
            {review.reviewData &&
              !isApproved &&
              calculateValidityStatus(review.reviewData.auditPeriodEnd).status === 'caution' && (
                <div className="mt-4 flex items-start gap-3 rounded-md bg-amber-500/10 px-4 py-3 text-amber-700">
                  <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <span className="font-medium">Action recommended:</span> This report is expiring soon or has
                    recently expired. Consider requesting a bridge report from this vendor after publishing the review.
                  </div>
                </div>
              )}
            {/* Bridge letter upload entry point — report expired, no active bridge letter */}
            {canUploadBridge && (
              <div className="mt-4 flex flex-col gap-3 rounded-md bg-blue-500/10 px-4 py-3 text-blue-700 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-3">
                  <FileUp className="h-5 w-5 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <span className="font-medium">Report expired:</span> Attach a bridge letter to keep this review
                    usable until the vendor&apos;s next report.
                  </div>
                </div>
                <Button size="sm" className="flex-shrink-0" onClick={() => setBridgeModalOpen(true)}>
                  <FileUp className="h-4 w-4 mr-1" />
                  Upload Bridge Letter
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Bridge Letter Card */}
        {review.bridgeLetter && (
          <BridgeLetterCard
            bridgeLetter={review.bridgeLetter}
            auditPeriodEnd={bridgeAuditPeriodEnd}
            vendorId={review.vendorId}
            isLatestReview={review.isLatestReview}
          />
        )}

        {/* Extracted Data + Auditor Info — side by side */}
        {displayData && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Extracted Data Card — 2/3 width */}
            <Card className="lg:col-span-2">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-medium">
                  {isEditing ? 'Edit Extracted Data' : 'Extracted Data'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Half-width fields in a grid */}
                <div className="grid grid-cols-2 gap-x-4">
                  <DataField
                    fieldKey="reportType"
                    label="Report Type"
                    value={displayData.reportType}
                    isEditing={isEditing}
                    onUpdate={handleFieldUpdate}
                  />
                  <DataField
                    fieldKey="vendorName"
                    label="Vendor Name"
                    value={displayData.vendorName}
                    isEditing={isEditing}
                    onUpdate={handleFieldUpdate}
                  />
                  <DataField
                    fieldKey="auditPeriodStart"
                    label="Audit Period Start"
                    value={displayData.auditPeriodStart}
                    isEditing={isEditing}
                    onUpdate={handleFieldUpdate}
                  />
                  <DataField
                    fieldKey="auditPeriodEnd"
                    label="Audit Period End"
                    value={displayData.auditPeriodEnd}
                    isEditing={isEditing}
                    onUpdate={handleFieldUpdate}
                  />
                  <DataField
                    fieldKey="productName"
                    label="Product Name"
                    value={displayData.productName}
                    isEditing={isEditing}
                    onUpdate={handleFieldUpdate}
                  />
                  <DataField
                    fieldKey="auditorOpinion"
                    label="Auditor Opinion"
                    value={displayData.auditorOpinion}
                    isEditing={isEditing}
                    onUpdate={handleFieldUpdate}
                  />
                </div>
                {/* Full-width fields */}
                <DataField
                  fieldKey="reportIssueDate"
                  label="Report Issue Date"
                  value={displayData.reportIssueDate}
                  isEditing={isEditing}
                  onUpdate={handleFieldUpdate}
                />
                <DataField
                  fieldKey="findings"
                  label="Findings"
                  value={displayData.findings}
                  isEditing={isEditing}
                  onUpdate={handleFieldUpdate}
                />
                <DataField
                  fieldKey="trustCriteria"
                  label="Trust Criteria"
                  value={displayData.trustCriteria}
                  isEditing={isEditing}
                  onUpdate={handleFieldUpdate}
                />
                <DataField
                  fieldKey="productDescription"
                  label="Product Description"
                  value={displayData.productDescription}
                  isEditing={isEditing}
                  onUpdate={handleFieldUpdate}
                />
                <DataField
                  fieldKey="subservicesUsage"
                  label="Subservices Usage"
                  value={displayData.subservicesUsage}
                  isEditing={isEditing}
                  onUpdate={handleFieldUpdate}
                />
                <DataField
                  fieldKey="auditorConclusion"
                  label="Auditor Conclusion"
                  value={displayData.auditorConclusion}
                  isEditing={isEditing}
                  onUpdate={handleFieldUpdate}
                />
                <DataField
                  fieldKey="conclusionSummary"
                  label="Conclusion Summary"
                  value={displayData.conclusionSummary}
                  isEditing={isEditing}
                  onUpdate={handleFieldUpdate}
                />
                <DataField
                  fieldKey="userEntityControls"
                  label="User Entity Controls"
                  value={displayData.userEntityControls}
                  isEditing={isEditing}
                  onUpdate={handleFieldUpdate}
                />
              </CardContent>
            </Card>

            {/* Auditor Info Card */}
            <Card className="h-fit">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-medium">Auditor Info</CardTitle>
                  {!isEditing && review.auditorId && (
                    <Link to={`/auditors/${review.auditorId}`} className="text-sm text-primary hover:underline">
                      View Auditor
                    </Link>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* CPA Firm / Auditor selector */}
                <div>
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">CPA Firm</div>
                  {isEditing ? (
                    <select
                      value={editedAuditorId ?? ''}
                      onChange={(e) => setEditedAuditorId(e.target.value)}
                      className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      {(auditorsData?.auditors ?? []).map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name} ({a.status})
                        </option>
                      ))}
                    </select>
                  ) : (
                    <p className="text-sm">
                      {review.auditorName || <span className="text-muted-foreground">Unknown</span>}
                    </p>
                  )}
                </div>

                {/* Auditor credibility — editable via DataField */}
                <DataField
                  fieldKey="auditorCredibility"
                  label="Auditor Self-Stated Credibility"
                  value={displayData?.auditorCredibility}
                  isEditing={isEditing}
                  onUpdate={handleFieldUpdate}
                />

                {/* Riveron's opinion — read-only, comes from auditor catalog */}
                <div>
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                    Riveron&apos;s Opinion
                  </div>
                  {review.auditorId && review.auditorStatus === 'published' && review.auditorOpinion ? (
                    <p className="text-sm whitespace-pre-wrap">{review.auditorOpinion}</p>
                  ) : review.auditorId && review.auditorStatus === 'draft' ? (
                    <p className="text-sm text-muted-foreground italic">
                      The linked auditor has not been published yet. Once published, Riveron&apos;s qualified opinion
                      will appear here.
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">
                      {review.auditorId ? 'No opinion available.' : 'No auditor linked to this review.'}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* No data state */}
        {!displayData && review.status !== 'pending' && review.status !== 'extracting' && (
          <Card>
            <CardContent className="py-8 text-center">
              <p className="text-muted-foreground">No extracted data available.</p>
            </CardContent>
          </Card>
        )}

        {/* Edit History Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <History className="h-4 w-4" />
              Edit History
            </CardTitle>
          </CardHeader>
          <CardContent>
            <EditHistoryTimeline
              edits={historyData?.edits || []}
              emptyMessage="No changes have been made to this review yet."
            />
          </CardContent>
        </Card>

        {/* Documents Section */}
        <DocumentsSection
          reviewId={review.id}
          vendorName={review.vendorName || 'Unknown Vendor'}
          reviewStatus={review.status}
          auditPeriodEnd={review.reviewData?.auditPeriodEnd ?? null}
          archivedAt={review.archivedAt}
          auditorStatus={review.auditorStatus}
          bridgeLetter={review.bridgeLetter}
        />
      </div>

      {/* Approve Confirmation Dialog */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Review</DialogTitle>
            <DialogDescription>
              Are you sure you want to approve this review? Once approved, the data cannot be edited.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApproveDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleApprove} disabled={approveMutation.isPending}>
              {approveMutation.isPending ? <Spinner size="sm" className="mr-2" /> : null}
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive Confirmation Dialog */}
      <Dialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive Review</DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-3">
                <p>
                  Are you sure you want to archive this review? This action is <strong>permanent</strong> and cannot be
                  undone.
                </p>
                <ul className="list-disc list-inside text-sm space-y-1">
                  <li>No further edits will be allowed</li>
                  <li>Documents cannot be generated</li>
                  <li>Drata uploads cannot be performed</li>
                  <li>This review will be hidden from the default list view</li>
                </ul>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowArchiveDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleArchive} disabled={archiveMutation.isPending}>
              {archiveMutation.isPending ? <Spinner size="sm" className="mr-2" /> : null}
              Archive Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Bridge Letter Modal */}
      {review.reviewData?.auditPeriodEnd && (
        <UploadBridgeLetterModal
          open={bridgeModalOpen}
          onOpenChange={setBridgeModalOpen}
          reviewId={review.id}
          vendorId={review.vendorId}
          auditPeriodEnd={review.reviewData.auditPeriodEnd}
          vendorName={review.vendorName ?? undefined}
        />
      )}
    </AppLayout>
  )
}
